import { useMutation, useQueryClient } from "@tanstack/react-query";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";

export interface DuplicateWorkflowRequest {
  workflowId: number;
  workspaceId: number;
  folderId?: number;
}

export interface DuplicateWorkflowResponse {
  success: boolean;
  workflow: {
    id: number;
    name: string;
    workspace_id: number;
    user_id: number;
    is_draft: boolean;
    is_deleted: boolean;
    folder_id: number | null;
  };
  workflow_id: number;
}

export const useDuplicateWorkflow = (
  onSuccessCallback?: (data: DuplicateWorkflowResponse) => void,
) => {
  const queryClient = useQueryClient();
  const authContext = useAuth();

  return useMutation<DuplicateWorkflowResponse, Error, DuplicateWorkflowRequest>({
    mutationFn: async ({ workflowId, workspaceId, folderId }) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 35000);

      try {
        const response = await fetch(`${API_URL}/duplicate-workflow`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authContext?.userToken}`,
          },
          credentials: "include",
          body: JSON.stringify({
            workflow_id: workflowId,
            workspace_id: workspaceId,
            ...(folderId !== undefined && { folder_id: folderId }),
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to duplicate workflow");
        }

        return response.json();
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === "AbortError") {
          throw new Error("Request timed out");
        }
        throw error;
      }
    },
    onSuccess: (data, { workspaceId }) => {
      displayToast("Workflow duplicated successfully", ToastType.success);

      queryClient.invalidateQueries({
        queryKey: ["workflows"],
      });

      queryClient.invalidateQueries({
        queryKey: ["workflows", { workspace_id: workspaceId }],
      });

      if (onSuccessCallback) {
        onSuccessCallback(data);
      }
    },
    onError: error => {
      displayErrorToast(error.message || "Failed to duplicate workflow. Please try again.");
    },
  });
};
